import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Sparkles, Save, Star, Plus, Trash2, Upload, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const HeroFeaturesEditor = () => {
  const [hero, setHero] = useState({ 
    title: '', 
    subtitle: '',
    titleColor: '#1f2937',
    titleFontSize: '48',
    titleFontFamily: 'system-ui',
    subtitleColor: '#4b5563',
    subtitleFontSize: '20',
    subtitleFontFamily: 'system-ui'
  });
  const [featuresSection, setFeaturesSection] = useState({
    title: 'Why Choose Us?',
    titleColor: '#1f2937',
    titleFontSize: '36',
    titleFontFamily: 'system-ui'
  });
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const [heroRes, featuresRes, featuresSectionRes] = await Promise.all([
        axios.get(`${API_URL}/business-info/hero_section`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/business-info/features_info`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/business-info/features_section`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { info_value: '{}' } })),
      ]);

      const loadedHero = JSON.parse(heroRes.data.info_value || '{}');
      setHero({
        title: loadedHero.title || '',
        subtitle: loadedHero.subtitle || '',
        titleColor: loadedHero.titleColor || '#1f2937',
        titleFontSize: loadedHero.titleFontSize || '48',
        titleFontFamily: loadedHero.titleFontFamily || 'system-ui',
        subtitleColor: loadedHero.subtitleColor || '#4b5563',
        subtitleFontSize: loadedHero.subtitleFontSize || '20',
        subtitleFontFamily: loadedHero.subtitleFontFamily || 'system-ui',
        bookNowLabel: loadedHero.bookNowLabel || 'Book Now',
        bookNowBgColor: loadedHero.bookNowBgColor || '#ffffff',
        bookNowTextColor: loadedHero.bookNowTextColor || '#2563eb',
        bookNowFontSize: loadedHero.bookNowFontSize || '18',
        myApptsLabel: loadedHero.myApptsLabel || 'My Appointments',
        myApptsBgColor: loadedHero.myApptsBgColor || '#ffffff',
        myApptsTextColor: loadedHero.myApptsTextColor || '#2563eb',
        myApptsFontSize: loadedHero.myApptsFontSize || '18',
        myProfileLabel: loadedHero.myProfileLabel || 'My Profile',
        myProfileBgColor: loadedHero.myProfileBgColor || '#ffffff',
        myProfileTextColor: loadedHero.myProfileTextColor || '#2563eb',
        myProfileFontSize: loadedHero.myProfileFontSize || '18',
      });
      
      const loadedFeaturesSection = JSON.parse(featuresSectionRes.data.info_value || '{}');
      setFeaturesSection({
        title: loadedFeaturesSection.title || 'Why Choose Us?',
        titleColor: loadedFeaturesSection.titleColor || '#1f2937',
        titleFontSize: loadedFeaturesSection.titleFontSize || '36',
        titleFontFamily: loadedFeaturesSection.titleFontFamily || 'system-ui'
      });
      
      const loadedFeatures = JSON.parse(featuresRes.data.info_value || '[]');
      const defaultEmojis = ['📅', '❤️', '🕐', '⭐', '🐕', '✂️'];
      setFeatures(loadedFeatures.map((f, idx) => ({
        title: f.title || '',
        description: f.description || '',
        titleColor: f.titleColor || '#1f2937',
        titleFontSize: f.titleFontSize || '20',
        titleFontFamily: f.titleFontFamily || 'system-ui',
        descriptionColor: f.descriptionColor || '#4b5563',
        descriptionFontSize: f.descriptionFontSize || '16',
        descriptionFontFamily: f.descriptionFontFamily || 'system-ui',
        iconType: f.iconType || 'emoji',
        iconValue: f.iconValue || defaultEmojis[idx % defaultEmojis.length],
        imageUrl: f.imageUrl || ''
      })));
    } catch (error) {
      console.error('Load data error:', error);
      // Set defaults
      setHero({
        title: 'Professional Dog Grooming Services',
        subtitle: 'Book your appointment online and give your furry friend the care they deserve',
        titleColor: '#1f2937',
        titleFontSize: '48',
        titleFontFamily: 'system-ui',
        subtitleColor: '#4b5563',
        subtitleFontSize: '20',
        subtitleFontFamily: 'system-ui',
        bookNowLabel: 'Book Now',
        bookNowBgColor: '#ffffff',
        bookNowTextColor: '#2563eb',
        bookNowFontSize: '18',
        myApptsLabel: 'My Appointments',
        myApptsBgColor: '#ffffff',
        myApptsTextColor: '#2563eb',
        myApptsFontSize: '18',
        myProfileLabel: 'My Profile',
        myProfileBgColor: '#ffffff',
        myProfileTextColor: '#2563eb',
        myProfileFontSize: '18',
      });
      setFeaturesSection({
        title: 'Why Choose Us?',
        titleColor: '#1f2937',
        titleFontSize: '36',
        titleFontFamily: 'system-ui'
      });
      setFeatures([
        { 
          title: 'Easy Online Booking', 
          description: 'Schedule appointments 24/7 with our user-friendly booking system. Choose your preferred groomer and time slot.',
          titleColor: '#1f2937',
          titleFontSize: '20',
          titleFontFamily: 'system-ui',
          descriptionColor: '#4b5563',
          descriptionFontSize: '16',
          descriptionFontFamily: 'system-ui',
          iconType: 'emoji',
          iconValue: '📅',
          imageUrl: ''
        },
        { 
          title: 'Expert Care', 
          description: 'Our experienced groomers treat every pet with love and care, ensuring a stress-free grooming experience.',
          titleColor: '#1f2937',
          titleFontSize: '20',
          titleFontFamily: 'system-ui',
          descriptionColor: '#4b5563',
          descriptionFontSize: '16',
          descriptionFontFamily: 'system-ui',
          iconType: 'emoji',
          iconValue: '❤️',
          imageUrl: ''
        },
        { 
          title: 'Flexible Scheduling', 
          description: 'Real-time availability updates and automatic reminders ensure you never miss an appointment.',
          titleColor: '#1f2937',
          titleFontSize: '20',
          titleFontFamily: 'system-ui',
          descriptionColor: '#4b5563',
          descriptionFontSize: '16',
          descriptionFontFamily: 'system-ui',
          iconType: 'emoji',
          iconValue: '🕐',
          imageUrl: ''
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureChange = (index, field, value) => {
    const updated = [...features];
    updated[index][field] = value;
    setFeatures(updated);
  };

  const handleSaveHero = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const heroData = JSON.stringify(hero);
      console.log('[HeroFeaturesEditor] Saving hero data:', hero);
      console.log('[HeroFeaturesEditor] Stringified data length:', heroData.length);
      const response = await axios.put(
        `${API_URL}/business-info/hero_section`,
        { value: heroData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('[HeroFeaturesEditor] Save response:', response.data);
      toast.success('Hero section updated successfully!');
    } catch (error) {
      console.error('Save hero error:', error);
      toast.error('Failed to save hero section');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFeatures = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      console.log('[HeroFeaturesEditor] Saving features:', features);
      console.log('[HeroFeaturesEditor] Saving features section:', featuresSection);
      const responses = await Promise.all([
        axios.put(
          `${API_URL}/business-info/features_info`,
          { value: JSON.stringify(features) },
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.put(
          `${API_URL}/business-info/features_section`,
          { value: JSON.stringify(featuresSection) },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);
      console.log('[HeroFeaturesEditor] Save responses:', responses.map(r => r.data));
      toast.success('Features updated successfully!');
    } catch (error) {
      console.error('Save features error:', error);
      toast.error('Failed to save features');
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    setFeatures([...features, {
      title: '',
      description: '',
      titleColor: '#1f2937',
      titleFontSize: '20',
      titleFontFamily: 'system-ui',
      descriptionColor: '#4b5563',
      descriptionFontSize: '16',
      descriptionFontFamily: 'system-ui',
      iconType: 'emoji',
      iconValue: '⭐',
      imageUrl: ''
    }]);
  };

  const removeFeature = (index) => {
    if (features.length <= 1) {
      toast.error('You must have at least one feature');
      return;
    }
    setFeatures(features.filter((_, i) => i !== index));
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/upload/feature-icon`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      const updated = [...features];
      updated[index].imageUrl = response.data.url;
      updated[index].iconType = 'image';
      setFeatures(updated);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    }
  };

  const removeImage = (index) => {
    const updated = [...features];
    updated[index].imageUrl = '';
    updated[index].iconType = 'emoji';
    setFeatures(updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Section Editor */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <Sparkles className="text-primary-600" size={24} />
          <h2 className="text-2xl font-bold">Hero Section (Top of Homepage)</h2>
        </div>

        <p className="text-gray-600 mb-6">
          Edit the main headline and subtitle that visitors see first when they land on your homepage.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Main Title</label>
            <input
              type="text"
              value={hero.title}
              onChange={(e) => setHero({ ...hero, title: e.target.value })}
              className="input text-lg font-semibold"
              placeholder="Professional Dog Grooming Services"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
            <textarea
              value={hero.subtitle}
              onChange={(e) => setHero({ ...hero, subtitle: e.target.value })}
              rows={2}
              className="input resize-none"
              placeholder="Book your appointment online and give your furry friend the care they deserve"
            />
          </div>

          {/* Title Styling */}
          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold text-gray-700 mb-3">Title Styling</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
                <select
                  value={hero.titleFontFamily}
                  onChange={(e) => setHero({ ...hero, titleFontFamily: e.target.value })}
                  className="input"
                >
                  <option value="system-ui">System UI</option>
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="'Times New Roman', serif">Times New Roman</option>
                  <option value="'Courier New', monospace">Courier New</option>
                  <option value="Georgia, serif">Georgia</option>
                  <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                  <option value="Impact, fantasy">Impact</option>
                  <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                  <option value="Verdana, sans-serif">Verdana</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Size (px)</label>
                <input
                  type="number"
                  value={hero.titleFontSize}
                  onChange={(e) => setHero({ ...hero, titleFontSize: e.target.value })}
                  min="12"
                  max="96"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={hero.titleColor}
                    onChange={(e) => setHero({ ...hero, titleColor: e.target.value })}
                    className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={hero.titleColor}
                    onChange={(e) => setHero({ ...hero, titleColor: e.target.value })}
                    className="input flex-1"
                    placeholder="#1f2937"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Subtitle Styling */}
          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold text-gray-700 mb-3">Subtitle Styling</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
                <select
                  value={hero.subtitleFontFamily}
                  onChange={(e) => setHero({ ...hero, subtitleFontFamily: e.target.value })}
                  className="input"
                >
                  <option value="system-ui">System UI</option>
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="'Times New Roman', serif">Times New Roman</option>
                  <option value="'Courier New', monospace">Courier New</option>
                  <option value="Georgia, serif">Georgia</option>
                  <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                  <option value="Impact, fantasy">Impact</option>
                  <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                  <option value="Verdana, sans-serif">Verdana</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Size (px)</label>
                <input
                  type="number"
                  value={hero.subtitleFontSize}
                  onChange={(e) => setHero({ ...hero, subtitleFontSize: e.target.value })}
                  min="12"
                  max="48"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={hero.subtitleColor}
                    onChange={(e) => setHero({ ...hero, subtitleColor: e.target.value })}
                    className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={hero.subtitleColor}
                    onChange={(e) => setHero({ ...hero, subtitleColor: e.target.value })}
                    className="input flex-1"
                    placeholder="#4b5563"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Button Settings */}
        <div className="border-t pt-4 mt-4 space-y-6">
          <h3 className="font-semibold text-gray-700">Hero Buttons</h3>

          {/* Book Now Button */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-gray-700 text-sm">Book Now Button</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={hero.bookNowLabel}
                  onChange={(e) => setHero({ ...hero, bookNowLabel: e.target.value })}
                  className="input"
                  placeholder="Book Now"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                <div className="flex space-x-2">
                  <input type="color" value={hero.bookNowBgColor} onChange={(e) => setHero({ ...hero, bookNowBgColor: e.target.value })} className="h-10 w-14 rounded border border-gray-300 cursor-pointer" />
                  <input type="text" value={hero.bookNowBgColor} onChange={(e) => setHero({ ...hero, bookNowBgColor: e.target.value })} className="input flex-1 font-mono text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                <div className="flex space-x-2">
                  <input type="color" value={hero.bookNowTextColor} onChange={(e) => setHero({ ...hero, bookNowTextColor: e.target.value })} className="h-10 w-14 rounded border border-gray-300 cursor-pointer" />
                  <input type="text" value={hero.bookNowTextColor} onChange={(e) => setHero({ ...hero, bookNowTextColor: e.target.value })} className="input flex-1 font-mono text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Size (px)</label>
                <input type="number" value={hero.bookNowFontSize} onChange={(e) => setHero({ ...hero, bookNowFontSize: e.target.value })} min="12" max="32" className="input" />
              </div>
            </div>
          </div>

          {/* My Profile Button */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-gray-700 text-sm">My Profile Button <span className="text-xs text-gray-400 font-normal">(only shown to logged-in clients)</span></h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input type="text" value={hero.myProfileLabel} onChange={(e) => setHero({ ...hero, myProfileLabel: e.target.value })} className="input" placeholder="My Profile" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                <div className="flex space-x-2">
                  <input type="color" value={hero.myProfileBgColor} onChange={(e) => setHero({ ...hero, myProfileBgColor: e.target.value })} className="h-10 w-14 rounded border border-gray-300 cursor-pointer" />
                  <input type="text" value={hero.myProfileBgColor} onChange={(e) => setHero({ ...hero, myProfileBgColor: e.target.value })} className="input flex-1 font-mono text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                <div className="flex space-x-2">
                  <input type="color" value={hero.myProfileTextColor} onChange={(e) => setHero({ ...hero, myProfileTextColor: e.target.value })} className="h-10 w-14 rounded border border-gray-300 cursor-pointer" />
                  <input type="text" value={hero.myProfileTextColor} onChange={(e) => setHero({ ...hero, myProfileTextColor: e.target.value })} className="input flex-1 font-mono text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Size (px)</label>
                <input type="number" value={hero.myProfileFontSize} onChange={(e) => setHero({ ...hero, myProfileFontSize: e.target.value })} min="12" max="32" className="input" />
              </div>
            </div>
          </div>

          {/* My Appointments Button */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-gray-700 text-sm">My Appointments Button <span className="text-xs text-gray-400 font-normal">(only shown to logged-in clients)</span></h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={hero.myApptsLabel}
                  onChange={(e) => setHero({ ...hero, myApptsLabel: e.target.value })}
                  className="input"
                  placeholder="My Appointments"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                <div className="flex space-x-2">
                  <input type="color" value={hero.myApptsBgColor} onChange={(e) => setHero({ ...hero, myApptsBgColor: e.target.value })} className="h-10 w-14 rounded border border-gray-300 cursor-pointer" />
                  <input type="text" value={hero.myApptsBgColor} onChange={(e) => setHero({ ...hero, myApptsBgColor: e.target.value })} className="input flex-1 font-mono text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                <div className="flex space-x-2">
                  <input type="color" value={hero.myApptsTextColor} onChange={(e) => setHero({ ...hero, myApptsTextColor: e.target.value })} className="h-10 w-14 rounded border border-gray-300 cursor-pointer" />
                  <input type="text" value={hero.myApptsTextColor} onChange={(e) => setHero({ ...hero, myApptsTextColor: e.target.value })} className="input flex-1 font-mono text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Size (px)</label>
                <input type="number" value={hero.myApptsFontSize} onChange={(e) => setHero({ ...hero, myApptsFontSize: e.target.value })} min="12" max="32" className="input" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={handleSaveHero}
            disabled={saving}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Save size={18} />
            <span>{saving ? 'Saving...' : 'Save Hero Section'}</span>
          </button>
        </div>
      </div>

      {/* Features Section Editor */}
      <div className="card">
        <div className="flex items-center space-x-3 mb-4">
          <Star className="text-primary-600" size={24} />
          <h2 className="text-2xl font-bold">"Why Choose Us?" Features</h2>
        </div>

        <p className="text-gray-600 mb-6">
          Customize the section title, feature cards, icons, and styling that highlight why customers should choose your business.
        </p>

        {/* Section Title Editor */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-gray-700 mb-3">Section Title</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title Text</label>
              <input
                type="text"
                value={featuresSection.title}
                onChange={(e) => setFeaturesSection({ ...featuresSection, title: e.target.value })}
                className="input"
                placeholder="Why Choose Us?"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
                <select
                  value={featuresSection.titleFontFamily}
                  onChange={(e) => setFeaturesSection({ ...featuresSection, titleFontFamily: e.target.value })}
                  className="input"
                >
                  <option value="system-ui">System UI</option>
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="'Times New Roman', serif">Times New Roman</option>
                  <option value="'Courier New', monospace">Courier New</option>
                  <option value="Georgia, serif">Georgia</option>
                  <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                  <option value="Impact, fantasy">Impact</option>
                  <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                  <option value="Verdana, sans-serif">Verdana</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Size (px)</label>
                <input
                  type="number"
                  value={featuresSection.titleFontSize}
                  onChange={(e) => setFeaturesSection({ ...featuresSection, titleFontSize: e.target.value })}
                  min="12"
                  max="72"
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={featuresSection.titleColor}
                    onChange={(e) => setFeaturesSection({ ...featuresSection, titleColor: e.target.value })}
                    className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={featuresSection.titleColor}
                    onChange={(e) => setFeaturesSection({ ...featuresSection, titleColor: e.target.value })}
                    className="input flex-1"
                    placeholder="#1f2937"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features List */}
        <div className="space-y-6">
          {features.map((feature, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-700">Feature {index + 1}</h3>
                <button
                  onClick={() => removeFeature(index)}
                  className="text-red-600 hover:text-red-700 flex items-center space-x-1"
                  title="Remove feature"
                >
                  <Trash2 size={18} />
                  <span className="text-sm">Remove</span>
                </button>
              </div>

              {/* Icon/Image Selection */}
              <div className="mb-4 p-3 bg-white rounded border border-gray-300">
                <label className="block text-sm font-medium text-gray-700 mb-2">Icon/Image</label>
                <div className="flex items-start space-x-4">
                  <div className="flex-1">
                    <div className="flex space-x-2 mb-3">
                      <button
                        onClick={() => handleFeatureChange(index, 'iconType', 'emoji')}
                        className={`px-4 py-2 rounded text-sm font-medium ${
                          feature.iconType === 'emoji'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Emoji
                      </button>
                      <button
                        onClick={() => handleFeatureChange(index, 'iconType', 'image')}
                        className={`px-4 py-2 rounded text-sm font-medium ${
                          feature.iconType === 'image'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Image
                      </button>
                    </div>
                    
                    {feature.iconType === 'emoji' ? (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Emoji (paste or type)</label>
                        <input
                          type="text"
                          value={feature.iconValue || ''}
                          onChange={(e) => {
                            console.log('Emoji input changed:', e.target.value);
                            handleFeatureChange(index, 'iconValue', e.target.value);
                          }}
                          onPaste={(e) => {
                            e.preventDefault();
                            const pastedText = e.clipboardData.getData('text');
                            console.log('Emoji pasted:', pastedText);
                            handleFeatureChange(index, 'iconValue', pastedText);
                          }}
                          className="input text-3xl text-center bg-white"
                          placeholder="📅"
                          maxLength="10"
                          autoComplete="off"
                          spellCheck="false"
                        />
                        <div className="mt-2">
                          <p className="text-xs text-gray-600 mb-1">Click to use:</p>
                          <div className="flex flex-wrap gap-2">
                            {['📅', '❤️', '🕐', '⭐', '🐕', '✂️', '🎯', '✅', '💎', '🏆', '🐾', '🎉', '🌟', '💪', '👍', '🔔'].map((emoji) => (
                              <button
                                key={emoji}
                                type="button"
                                onClick={() => handleFeatureChange(index, 'iconValue', emoji)}
                                className="text-2xl hover:bg-gray-100 p-2 rounded transition-colors"
                                title={`Use ${emoji}`}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {feature.imageUrl ? (
                          <div className="relative">
                            <img
                              src={feature.imageUrl}
                              alt="Feature icon"
                              className="w-24 h-24 object-cover rounded border border-gray-300"
                            />
                            <button
                              onClick={() => removeImage(index)}
                              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Upload Image or Enter URL</label>
                            <div className="flex space-x-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(index, e.target.files[0])}
                                className="hidden"
                                id={`image-upload-${index}`}
                              />
                              <label
                                htmlFor={`image-upload-${index}`}
                                className="btn bg-gray-200 text-gray-700 hover:bg-gray-300 cursor-pointer flex items-center space-x-1"
                              >
                                <Upload size={16} />
                                <span>Upload</span>
                              </label>
                              <input
                                type="text"
                                value={feature.imageUrl}
                                onChange={(e) => handleFeatureChange(index, 'imageUrl', e.target.value)}
                                className="input flex-1"
                                placeholder="Or paste image URL"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Preview */}
                  <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                    {feature.iconType === 'emoji' ? (
                      <span className="text-4xl">{feature.iconValue || '⭐'}</span>
                    ) : feature.imageUrl ? (
                      <img
                        src={feature.imageUrl}
                        alt="Preview"
                        className="w-16 h-16 object-cover rounded-full"
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">No image</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Title and Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={feature.title}
                    onChange={(e) => handleFeatureChange(index, 'title', e.target.value)}
                    className="input"
                    placeholder="e.g., Easy Online Booking"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={feature.description}
                    onChange={(e) => handleFeatureChange(index, 'description', e.target.value)}
                    rows={2}
                    className="input resize-none"
                    placeholder="Brief description of this feature"
                  />
                </div>
              </div>

              {/* Title Styling */}
              <div className="border-t pt-3 mt-3">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Title Styling</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Font Family</label>
                    <select
                      value={feature.titleFontFamily}
                      onChange={(e) => handleFeatureChange(index, 'titleFontFamily', e.target.value)}
                      className="input text-sm"
                    >
                      <option value="system-ui">System UI</option>
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="'Times New Roman', serif">Times New Roman</option>
                      <option value="'Courier New', monospace">Courier New</option>
                      <option value="Georgia, serif">Georgia</option>
                      <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                      <option value="Impact, fantasy">Impact</option>
                      <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                      <option value="Verdana, sans-serif">Verdana</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Font Size (px)</label>
                    <input
                      type="number"
                      value={feature.titleFontSize}
                      onChange={(e) => handleFeatureChange(index, 'titleFontSize', e.target.value)}
                      min="12"
                      max="48"
                      className="input text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Text Color</label>
                    <div className="flex space-x-1">
                      <input
                        type="color"
                        value={feature.titleColor}
                        onChange={(e) => handleFeatureChange(index, 'titleColor', e.target.value)}
                        className="h-9 w-16 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={feature.titleColor}
                        onChange={(e) => handleFeatureChange(index, 'titleColor', e.target.value)}
                        className="input flex-1 text-sm"
                        placeholder="#1f2937"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Description Styling */}
              <div className="border-t pt-3 mt-3">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Description Styling</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Font Family</label>
                    <select
                      value={feature.descriptionFontFamily}
                      onChange={(e) => handleFeatureChange(index, 'descriptionFontFamily', e.target.value)}
                      className="input text-sm"
                    >
                      <option value="system-ui">System UI</option>
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="'Times New Roman', serif">Times New Roman</option>
                      <option value="'Courier New', monospace">Courier New</option>
                      <option value="Georgia, serif">Georgia</option>
                      <option value="'Comic Sans MS', cursive">Comic Sans MS</option>
                      <option value="Impact, fantasy">Impact</option>
                      <option value="'Trebuchet MS', sans-serif">Trebuchet MS</option>
                      <option value="Verdana, sans-serif">Verdana</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Font Size (px)</label>
                    <input
                      type="number"
                      value={feature.descriptionFontSize}
                      onChange={(e) => handleFeatureChange(index, 'descriptionFontSize', e.target.value)}
                      min="12"
                      max="32"
                      className="input text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Text Color</label>
                    <div className="flex space-x-1">
                      <input
                        type="color"
                        value={feature.descriptionColor}
                        onChange={(e) => handleFeatureChange(index, 'descriptionColor', e.target.value)}
                        className="h-9 w-16 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={feature.descriptionColor}
                        onChange={(e) => handleFeatureChange(index, 'descriptionColor', e.target.value)}
                        className="input flex-1 text-sm"
                        placeholder="#4b5563"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add Feature Button */}
        <div className="mt-4">
          <button
            onClick={addFeature}
            className="btn bg-green-100 text-green-700 hover:bg-green-200 flex items-center space-x-2"
          >
            <Plus size={18} />
            <span>Add Feature</span>
          </button>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={handleSaveFeatures}
            disabled={saving}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Save size={18} />
            <span>{saving ? 'Saving...' : 'Save Features'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeroFeaturesEditor;
